'use client';

import { c as _c } from "react/compiler-runtime";
import { useVoteOnOutcomeSuggestion } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';
import { TOAST_IDS } from '@/config/toast-ids';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { showToast } from '@/lib/ui/toast';
import type { VoteKind } from './types';
interface SuggestionVoteModel {
  reaction: VoteKind | null;
  upCount: number;
  downCount: number;
  upAriaLabel: string;
  downAriaLabel: string;
  onReact: (next: VoteKind, e: MouseEvent<HTMLButtonElement>) => void;
}
interface LiveCounts {
  upvotes: number;
  downvotes: number;
  viewerKind: VoteKind | null;
}

/**
 * Shapes the outcome-suggestion vote arrows the same way
 * `useProposalVoteModel` shapes the proposal vote arrows so both surfaces
 * can drive the shared `<ReactionVoteButtons>` from a uniform model.
 *
 * Live counts (`upvotes` / `downvotes` / `viewerKind`) come from the SDK
 * row — `useGetOutcomeSuggestions` realtime patches `upvotes` / `downvotes`
 * over NATS via `applyOutcomeSuggestionVote`, and the optimistic
 * recipe `applyOutcomeSuggestionVoteOptimistic` flips `viewerKind`
 * instantly on click before the network round-trip.
 *
 * `onReact(direction, e)` emits the DIRECTION clicked; this hook decides
 * whether that's a set-vote or a clear-vote based on the current
 * `viewerKind`. Mirrors `useProposalVoteModel.onReact` exactly so the
 * shared button component's contract is identical.
 */
export function useSuggestionVoteModel(suggestionId, liveCounts, options) {
  const $ = _c(18);
  const t = useTranslations("launchpad.suggestedOutcomes");
  const {
    ensureAuthenticated
  } = useAuthGate();
  const voteMutation = useVoteOnOutcomeSuggestion();
  const reaction = liveCounts.viewerKind;
  const upCount = liveCounts.upvotes;
  const downCount = liveCounts.downvotes;
  let t0;
  if ($[0] !== ensureAuthenticated || $[1] !== options?.disabled || $[2] !== reaction || $[3] !== suggestionId || $[4] !== t || $[5] !== voteMutation) {
    t0 = function onReact(next, e) {
      e.preventDefault();
      e.stopPropagation();
      if (options?.disabled === true) {
        return;
      }
      if (!ensureAuthenticated()) {
        return;
      }
      const nextKind = reaction === next ? null : next;
      voteMutation.mutate({
        id: suggestionId,
        kind: nextKind as unknown as 'up' | 'down'
      }, {
        onSuccess: () => {
          if (nextKind === null) {
            showToast({
              id: TOAST_IDS.OUTCOME_SUGGESTION_VOTE_CLEARED,
              message: t("toastCleared"),
              variant: "success"
            });
            return;
          }
          showToast({
            id: TOAST_IDS.OUTCOME_SUGGESTION_VOTED,
            message: t(nextKind === "up" ? "toastUp" : "toastDown"),
            variant: "success"
          });
        },
        onError: () => {
          showToast({
            id: TOAST_IDS.OUTCOME_SUGGESTION_VOTE_ERROR,
            message: t("toastError"),
            variant: "error"
          });
        }
      });
    };
    $[0] = ensureAuthenticated;
    $[1] = options?.disabled;
    $[2] = reaction;
    $[3] = suggestionId;
    $[4] = t;
    $[5] = voteMutation;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const onReact = t0;
  let t1;
  if ($[7] !== t) {
    t1 = t("upvote");
    $[7] = t;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== t) {
    t2 = t("downvote");
    $[9] = t;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== downCount || $[12] !== onReact || $[13] !== reaction || $[14] !== t1 || $[15] !== t2 || $[16] !== upCount) {
    t3 = {
      reaction,
      upCount,
      downCount,
      upAriaLabel: t1,
      downAriaLabel: t2,
      onReact
    };
    $[11] = downCount;
    $[12] = onReact;
    $[13] = reaction;
    $[14] = t1;
    $[15] = t2;
    $[16] = upCount;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
}